import React, { useState, useEffect } from 'react';
import { Button, Select, DatePicker, Input, Radio, message, Modal } from 'antd';
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import { TaskData } from '../../../../types/TaskData';
import './ConstructionOrderCreation.css';
import dayjs, { Dayjs } from "dayjs";
import LoadingModal from "../../../../components/loadingModal/LoadingModal";
import { StatusUtils } from "../../../../utils/StatusUtils";
import { getConfig } from "../../../../config";
import { ViewType } from "../../../../types/ViewType";
import { useProjectContext } from "../../../../context/ProjectContext";
import { useDirtyContext } from "../../../../context/DirtyContext";
import locale from "antd/es/date-picker/locale/de_DE";

const { Option } = Select;
const { TextArea } = Input;
const { API_BASE_URL } = getConfig();

interface ConstructionOrderCreationProps {
    data: TaskData[];
    viewType: ViewType;
    onSave?: (data: any) => void;
    projectNo?: string;
    measurementLinkNew?: string;
    onRefreshTable?: () => void;
}

const ConstructionOrderCreation: React.FC<ConstructionOrderCreationProps> = ({ data, viewType, onSave, onRefreshTable, measurementLinkNew, projectNo }) => {
    const [orderType, setOrderType] = useState<string>('Neu');
    const [systemPlanApprovalLink, setSystemPlanApprovalLink] = useState<string>('');
    const [facadePlanLink, setFacadePlanLink] = useState<string>('');
    const [measurementLink, setMeasurementLink] = useState<string>('');
    const [isGalvanized, setIsGalvanized] = useState<boolean | null>(null);
    const [jobCodes, setJobCodes] = useState<NetworkPlanJobCode[]>([]);
    const [mappedJobCode, setMappedJobCode] = useState<NetworkPlanJobCode | null>(null);
    const [isCoated, setIsCoated] = useState<boolean | null>(null);
    const [colorCode, setColorCode] = useState<string>('');
    const [effort, setEffort] = useState<number | null>(null);
    const [desiredDeliveryDate, setDesiredDeliveryDate] = useState<Dayjs | null>(null);
    const [comment, setComment] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [loadingCount, setLoadingCount] = useState(0);
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [availableColors, setAvailableColors] = useState<string[]>([]);
    const { selectedProject: contextProject } = useProjectContext();
    const { setDirty } = useDirtyContext();
    const projectToUse =
        projectNo && projectNo !== "Alle"
            ? projectNo
            : (data && data.length > 0 && data[0].projectNo)
                ? data[0].projectNo
                : contextProject;

    useEffect(() => {
        const isComplete =
            !!orderType &&
            isGalvanized !== null &&
            isCoated !== null &&
            (isCoated ? colorCode.trim() !== '' : true) &&
            effort !== null &&
            desiredDeliveryDate &&
            systemPlanApprovalLink.trim() !== '' &&
            facadePlanLink.trim() !== ''
        setIsFormComplete(!!isComplete);
    }, [
        orderType,
        isGalvanized,
        isCoated,
        colorCode,
        effort,
        desiredDeliveryDate,
        comment,
        systemPlanApprovalLink,
        facadePlanLink,
        measurementLink,
    ]);

    useEffect(() => {
        const fetchJobCodes = async () => {
            try {
                setLoadingCount(prev => prev + 1);
                const response = await axios.get(
                    `${API_BASE_URL}/getNetworkPlanJobCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectToUse}`
                );
                setJobCodes(response.data);
            } catch (error) {
                message.error('Fehler beim Abrufen der Job-Codes');
            } finally {
                setLoadingCount(prev => Math.max(0, prev - 1));
            }
        };
        fetchJobCodes();
    }, []);

    useEffect(() => {
        console.log("useEffect triggered with:", { data, jobCodes });
        const matchedJobCode = jobCodes.find((code) => code.alias === data[0]?.jobCode);
        setMappedJobCode(matchedJobCode || null);
        const fetchMeasurementLink = async () => {
            if (!data || data.length === 0) return;
            if (viewType === ViewType.NEW_EXTERNAL && measurementLinkNew !== undefined) {
                setMeasurementLink(measurementLinkNew);
            } else  {
                try {
                    setLoadingCount(prev => prev + 1);
                    const taskIds = data.map((task) => task.id);
                    const response = await axios.post(`${API_BASE_URL}/getMeasureDataLinks`, {
                        taskIds,
                    });
                    console.log(response)
                    if (response?.data) {
                        setMeasurementLink(response?.data || "Nicht relevant");
                    }
                } catch (error) {
                    console.error("Failed to fetch measurement link:", error);
                    setMeasurementLink("Nicht relevant");
                } finally {
                    setLoadingCount(prev => Math.max(0, prev - 1));
                }
            }
        };

        if (matchedJobCode) {
            setFacadePlanLink(matchedJobCode.heliosFacadeplan?.trim() || "Nicht relevant");
            setSystemPlanApprovalLink(matchedJobCode.heliosApprovedSystemPlan?.trim() || "Nicht relevant");
            if (matchedJobCode.colorCode) {
                const colors = matchedJobCode.colorCode.split('|');
                if (viewType !== ViewType.CHANGE) {
                    if (colors.length > 0) {
                        setColorCode(colors[0]);
                    }
                }
                setAvailableColors(colors);
            } else {
                setAvailableColors([]);
            }

            if (viewType !== ViewType.CHANGE) {
                setIsGalvanized(matchedJobCode.galvanized === '1');
                setIsCoated(matchedJobCode.hasSurface === '1');
                setEffort(matchedJobCode.drawingHours);

                if (data.length > 0 && data[0].faz) {
                    const firstTask = data[0];
                    let parsedDate;

                    if (viewType === ViewType.KORREX) {
                        setOrderType("Korrex");
                    } else {
                        setOrderType("Neu");
                    }

                    const preselectedDate = StatusUtils.calculateWorkingDays(10);
                    setDesiredDeliveryDate(preselectedDate);

                    if (typeof firstTask.faz === 'string') {
                        parsedDate = dayjs(firstTask.faz, 'DD.MM.YYYY');
                    } else if (typeof firstTask.faz === 'number') {
                        parsedDate = dayjs(firstTask.faz);
                    }

                    if (parsedDate && parsedDate.isValid()) {
                        setDesiredDeliveryDate(parsedDate);
                    } else {
                        console.error('Invalid date format:', firstTask.faz);
                    }
                }
            }

            fetchMeasurementLink();
        }
    }, [data, jobCodes]);

    useEffect(() => {
        if (viewType === ViewType.CHANGE && data.length > 0) {
            const taskId = data[0]?.id;
            if (!taskId) return;
            const fetchOrderDetails = async () => {
                try {
                    setLoadingCount(prev => prev + 1);
                    const response = await axios.get(
                        `${API_BASE_URL}/getSingleNetworkPlanOrder?taskId=${taskId}`
                    );
                    if (response.data) {
                        const { desiredDeliveryDate, comment, galvanized, coated, colorCode, effort } = response.data;
                        setIsGalvanized(galvanized);
                        setIsCoated(coated);
                        setColorCode(colorCode);
                        setEffort(effort ? parseInt(effort, 10) : null);
                        setComment(comment || "");

                        if (desiredDeliveryDate) {
                            const parsedDate = dayjs(desiredDeliveryDate, 'DD.MM.YYYY');
                            if (parsedDate.isValid()) {
                                setDesiredDeliveryDate(parsedDate);
                            } else {
                                console.error("Invalid desiredDeliveryDate format:", desiredDeliveryDate);
                            }
                        }
                    }
                } catch (error) {
                    message.error("Fehler beim Abrufen der Bestellung.");
                } finally {
                    setLoadingCount(prev => Math.max(0, prev - 1));
                }
            };
            fetchOrderDetails();
        }
    }, [viewType, data]);

    const validateHeliosUrl = (value: string) => {
        if (!value.startsWith("h") && value.length === 1) {
            message.error("Bitte gebe eine gültige Helios-URL an");
            return false;
        }
        return true;
    };

    const handleInputChange = (value: string, setValue: (value: (((prevState: string) => string) | string)) => void) => {
        setDirty(true);
        if (value === "h") {
            setValue("helios://");
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            setValue(value);
        }
    };

    const handleSave = async () => {
        if (!isFormComplete) {
            message.warning("Bitte fülle alle erforderlichen Felder aus.");
            return;
        }

        const payload = {
            taskIds: data.map(task => task.id),
            orderType,
            systemPlanApprovalLink,
            facadePlanLink,
            measurementLink,
            isGalvanized,
            isCoated,
            colorCode: isCoated ? colorCode : null,
            effort,
            desiredDeliveryDate: desiredDeliveryDate?.format('DD.MM.YYYY'),
            comment,
        };

        if (viewType === ViewType.NEW_EXTERNAL && onSave) {
            onSave(payload);
            setIsSaved(true);
            return;
        }

        setSaving(true);
        try {
            if (viewType === ViewType.CHANGE) {
                await axios.post(`${API_BASE_URL}/changeNetworkPlanConstructionOrder`, payload);
            } else {
                await axios.post(`${API_BASE_URL}/createNewNetworkPlanConstructionOrder`, payload);
            }
            message.success('Konstruktions-Bestellung wurde erfolgreich erstellt.');
            if (onRefreshTable) {
                onRefreshTable();
            }
        } catch (error) {
            message.error('Konstruktions-Bestellung konnte nicht erstellt werden');
        } finally {
            setSaving(false);
            if (onRefreshTable) {
                onRefreshTable();
            }
        }
    };

    return (
        <div className="container">
            <LoadingModal open={loadingCount > 0} text="Daten werden geladen..." />
            <LoadingModal open={saving} text="Konstruktionsbeauftragung wird erstellt..." />

            <div className="input-group">
                <label><strong>Art der Beauftragung<span style={{ color: 'red' }}> *</span></strong></label>
                <Select disabled value={orderType}>
                    <Option value="Neu">Neu</Option>
                    <Option value="Korrex">Korrex</Option>
                </Select>
            </div>

            <div className="input-group">
                <label><strong>Freigabe Systemplan<span style={{ color: 'red' }}> *</span></strong></label>
                <Input
                    disabled={true}
                    value={systemPlanApprovalLink}
                    onChange={(e) => setSystemPlanApprovalLink(e.target.value)} />
            </div>

            <div className="input-group">
                <label><strong>Fassaden- und Grundrisspläne<span style={{ color: 'red' }}> *</span></strong></label>
                <Input
                    disabled={true}
                    value={facadePlanLink}
                    onChange={(e) => setFacadePlanLink(e.target.value)} />
            </div>

            <div className="input-group">
                <label><strong>Aufmass</strong></label>
                <Input
                    disabled={viewType !== ViewType.NEW_EXTERNAL}
                    value={measurementLink}
                    onChange={(e) => handleInputChange(e.target.value, setMeasurementLink)}
                />
            </div>

            <div className="input-group">
                <label><strong>Feuerverzinkt<span style={{ color: 'red' }}> *</span></strong></label>
                <Radio.Group value={isGalvanized} onChange={(e) => setIsGalvanized(e.target.value)}>
                    <Radio value={true}>Ja</Radio>
                    <Radio value={false}>Nein</Radio>
                </Radio.Group>
            </div>

            <div className="input-group">
                <label><strong>Beschichtung<span style={{ color: 'red' }}> *</span></strong></label>
                <Radio.Group value={isCoated} onChange={(e) => setIsCoated(e.target.value)}>
                    <Radio value={true}>Ja</Radio>
                    <Radio value={false}>Nein</Radio>
                </Radio.Group>


                {isCoated && (
                    <Select
                        style={{ width: '100%', marginTop: '10px' }}
                        placeholder="Farbcode auswählen"
                        value={colorCode}
                        onChange={(val) => setColorCode(val)}
                    >
                        {availableColors.map((color) => (
                            <Select.Option key={color} value={color}>
                                {color}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </div>
            <div className="input-group">
                <label><strong>Aufwand in Stunden<span style={{ color: 'red' }}> *</span></strong></label>
                <Input type="number" placeholder="Aufwand eingeben" value={effort || ''} onChange={(e) => setEffort(Number(e.target.value))} />
            </div>

            <div className="input-group">
                <label><strong>Gewünschtes Lieferdatum (DLZ: 10AT)<span style={{ color: 'red' }}> *</span></strong></label>
                <DatePicker
                    locale={locale}
                    value={desiredDeliveryDate}
                    onChange={setDesiredDeliveryDate}
                    placeholder="Auswahl Datum"
                    showToday
                    disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                    format="DD.MM.YYYY" />

            </div>

            <div className="input-group">
                <label><strong>Kommentar</strong></label>
                <TextArea rows={3} placeholder="Kommentar" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>

            <div style={{ textAlign: 'right' }}>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!isFormComplete || (viewType === ViewType.NEW_EXTERNAL && isSaved)}
                    className="action-button"
                >
                    {viewType === ViewType.NEW_EXTERNAL ? "Zwischenspeichern" : "Beauftragen"}
                </Button>
            </div>
        </div>
    );
};

export default ConstructionOrderCreation;
